import React, { useState } from "react";
import axios from "axios";

const EventForm = () => {
  const [title, setTitle] = useState("");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const imageLinks = [];
    setUploading(true);

    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append("image", files[i]);

      try {
        const response = await axios.post(
          "https://api.imgbb.com/1/upload?key=486efa604b729a5151b5e18abca412d5",
          formData,
        );
        if (response.data && response.data.data.url) {
          imageLinks.push(response.data.data.url);
        }
      } catch (error) {
        console.error("Error uploading image: ", error);
      }
    }

    setImages((prevImages) => [...prevImages, ...imageLinks]);
    setUploading(false);
  };

  const handleRemoveImage = (indexToRemove) => {
    setImages((prevImages) =>
      prevImages.filter((_, idx) => idx !== indexToRemove),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    setSubmitting(true);
    const eventData = {
      title,
      images,
    };

    try {
      await axios.post(
        "https://mosque-backend-yfae.vercel.app/events",
        eventData,
      );
      alert("Event created successfully!");
      setTitle("");
      setImages([]);
    } catch (error) {
      console.error("Error creating event: ", error);
      alert("Failed to create event. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-100 shadow-xl p-6 md:p-8">
      {/* Form Header */}
      <div className="text-center mb-8 pb-4 border-b border-slate-100">
        <h2 className="text-2xl font-serif font-bold text-slate-800 tracking-wide">
          Publish New Event
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Share new congregation updates, galleries, and events with the
          community
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title input field */}
        <div>
          <label className="block text-xs font-bold tracking-wider uppercase text-slate-600 mb-2">
            Event Title / Heading
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-3 bg-stone-50 border border-slate-200 focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-600/10 rounded-xl outline-none transition-all duration-200 text-slate-800"
            placeholder="e.g., Annual Seerat-un-Nabi Conference"
            required
          />
        </div>

        {/* Beautiful Styled Drag & Drop/Click Upload Area */}
        <div>
          <label className="block text-xs font-bold tracking-wider uppercase text-slate-600 mb-2">
            Upload Media Gallery
          </label>
          <div
            className={`relative border-2 border-dashed rounded-xl p-6 transition-all text-center flex flex-col items-center justify-center ${
              uploading
                ? "border-amber-400 bg-amber-50/20"
                : "border-slate-200 hover:border-emerald-600 bg-stone-50/50"
            }`}
          >
            <input
              type="file"
              id="file-upload"
              multiple
              onChange={handleFileChange}
              className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
              disabled={uploading || submitting}
            />

            {uploading ? (
              <div className="space-y-2">
                <div className="w-8 h-8 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto" />
                <p className="text-sm font-medium text-amber-700">
                  Syncing images to media cloud...
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4.5v15m7.5-7.5h-15"
                    />
                  </svg>
                </div>
                <p className="text-sm font-medium text-slate-700">
                  Click to drop or browse images
                </p>
                <p className="text-xs text-slate-400">
                  Supports multiple JPG, PNG selections
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Dynamic Interactive Image Previews */}
        {images.length > 0 && (
          <div className="bg-stone-50/50 p-4 rounded-xl border border-slate-100">
            <h3 className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-3">
              Uploaded Assets ({images.length})
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {images.map((image, index) => (
                <div
                  key={index}
                  className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-white"
                >
                  <img
                    src={image}
                    alt={`uploaded-${index}`}
                    className="w-full h-full object-cover"
                  />
                  {/* Delete hovering asset icon */}
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-1.5 right-1.5 p-1 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-md opacity-90 transition-opacity"
                    title="Remove image"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-3.5 h-3.5"
                    >
                      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Button Trigger */}
        <button
          type="submit"
          disabled={uploading || submitting || !title.trim()}
          className="w-full bg-emerald-800 hover:bg-emerald-700 active:bg-emerald-900 text-white font-medium tracking-wide py-3 px-4 rounded-xl shadow-lg shadow-emerald-800/20 transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Publishing Event...</span>
            </>
          ) : (
            <span>Publish Event</span>
          )}
        </button>
      </form>
    </div>
  );
};

export default EventForm;
